//Library Imports
import React, { useState, useEffect } from "react";
import styled from "styled-components";

//Local Imports
import { getQueryCampaignId } from "modules/EmailBrowser/utils";
import { resetEmailsConditionally, getEmailsConditionally } from "../actions";
import SentEmailSearch from "./SentEmailSearch";
import EmailTabs from "./EmailTabs";
import { EMAIL_TABS_MAP } from "constants/types/tabTypes";
import { RootState } from "store";
import { useSelector } from "react-redux";
import SentEditionsWrapper from "./SentEditionsWrapper";
import TestEditionsWrapper from "./TestEditionsWrapper";
import AllEditionsWrapper from "./AllEditionsWrapper";
import AllEditionsSentWrapper from "./AllEditionsSentWrapper";
import ScheduledEditions from "./ScheduledEditions";
import { EMAIL_TYPE_MAP } from "constants/index";

interface IEmailEditionsWrapper {
  enableTabs?: boolean;
  showSearchBar?: boolean;
  showEmailStats?: boolean;
  title?: string;
  date?: any;
  user?: any;
  emailType?: any;
}

const DEFAULT_PAGE_LIMIT = 20; //* later on api hits

const EmailEditionsWrapper: React.FC<IEmailEditionsWrapper> = (props: IEmailEditionsWrapper) => {
  //Others
  const { enableTabs, title, date, user, emailType } = props;
  const [activeHeader, setActiveHeader] = useState("send-header");
  const campaignId = getQueryCampaignId();

  //Sent Editions
  const [previousSentSearch, setPreviousSentSearch] = useState("");
  const [isSearch, setIsSearch] = useState(false);
  const [tabState, setTabState] = useState("sent");
  const totalEmailSent: number = useSelector((state: RootState) => state.emails?.sentEmails?.total);
  const totalEmailTest: number = useSelector((state: RootState) => state.emails?.testEmails?.total);
  const totalEmailScheduled: number = useSelector((state: RootState) => state.emails?.scheduledEmails?.total);
  const totalEmailAll: number = useSelector((state: RootState) => state.emails?.allEmails?.total);

  // email counts of tabs
  const counts: { [key: string]: number } = {
    sent: totalEmailSent,
    all: totalEmailAll,
    scheduled: totalEmailScheduled,
    test: totalEmailTest,
  };

  // email tabs
  const emailTabs: { [key: string]: string } = {
    sent: "Sent",
    all: "All",
    scheduled: "Scheduled",
    test: "Sent",
  };

  const getPagination = (isSearch = false) => {
    let limit = DEFAULT_PAGE_LIMIT; //update to 4
    const otherParams =
      enableTabs === true
        ? {
          ...(user.value === "default" ? {} : { user_id: user.value }),
          ...(enableTabs ? EMAIL_TABS_MAP[tabState] : {}),
          ...(EMAIL_TYPE_MAP[emailType.value] === "" ? {} : { email_type: EMAIL_TYPE_MAP[emailType.value] }),
          // ...(date && !isSearch && tabState !== "scheduled" ? date : {}),
        }
        : {};
    return {
      offset: 0,
      limit,
      asset_id: campaignId,
      version_status: emailTabs[tabState],
      ...(enableTabs ? EMAIL_TABS_MAP[tabState] : {}),
      ...otherParams,
    };
  };

  const searchEmails = (value: string) => {
    resetEmailsConditionally(enableTabs, tabState);
    setPreviousSentSearch(value.trim());
    setIsSearch(true);
    setActiveHeader("send-header");
    getEmailsConditionally(
      campaignId,
      { ...getPagination(true), search_term: value },
      false,
      undefined,
      enableTabs,
      tabState
    );
  };
  const handleSearch = (value: string) => {
    if (previousSentSearch === value.trim()) return;
    searchEmails(value);
  };

  const resetSearch = () => {
    resetEmailsConditionally(enableTabs, tabState);
    setIsSearch(false);
    getEmailsConditionally(campaignId, getPagination(), false, undefined, enableTabs, tabState);
    setPreviousSentSearch("");
  };

  useEffect(() => {
    if (previousSentSearch?.trim()?.length > 0) {
      searchEmails(previousSentSearch);
    }
  }, [tabState]);

  return (
    <>
      <HeadingWrapper className="d-flex flex-row justify-content-between align-items-center">
        <div className="d-flex">
          <h3 className={"campaign-dashboard-heading"}>{title ?? `Emails (${counts[tabState]})`}</h3>
          {enableTabs && <EmailTabs tabState={tabState} setTabState={setTabState} />}
        </div>
        <div className={"search-bar"}>
          <SentEmailSearch
            handleSearch={handleSearch}
            reset={resetSearch}
            placeholder="Search subject or sender email"
          />
        </div>
      </HeadingWrapper>
      {enableTabs ? (
        tabState === "sent" ? (
          <SentEditionsWrapper
            isSearch={isSearch}
            previousSentSearch={previousSentSearch}
            date={date}
            emailType={emailType}
            user={user}
          />
        ) : tabState === "scheduled" ? (
          <ScheduledEditions
            showTitle={false}
            isSearch={isSearch}
            previousSentSearch={previousSentSearch}
            removePagination={false}
            emailType={emailType}
            user={user}
            date={date}
          />
        ) : (
          // : tabState === "all" ? (
          //   <AllEditionsWrapper
          //     isSearch={isSearch}
          //     previousSentSearch={previousSentSearch}
          //     date={date}
          //     emailType={emailType}
          //     user={user}
          //   />
          // )
          <TestEditionsWrapper
            isSearch={isSearch}
            previousSentSearch={previousSentSearch}
            date={date}
            emailType={emailType}
            user={user}
          />
        )
      ) : (
        <AllEditionsSentWrapper isSearch={isSearch} previousSentSearch={previousSentSearch} />
      )}
    </>
  );
};

export default EmailEditionsWrapper;

EmailEditionsWrapper.defaultProps = {
  showSearchBar: true,
  showEmailStats: true,
  title: undefined,
  enableTabs: false,
};

const HeadingWrapper = styled.div`
  margin: 4px 0px 18px 0 !important;
  @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
    margin-top: 16px !important;
  }
  .editions-text-styling {
    font-size: 22px !important;
  }
  .search-bar {
    min-width: 260px;
    height: 34px;
  }
`;
